/** Spell research: the expansion's way of arguing with a mage guild.
 *
 * A guild rolls its spells once and keeps them forever, which is the classic's rule
 * and the reason a Tower with three bad level-5 slots is simply a bad Tower.
 * The expansion lets a town pay to replace one, and the price is deliberately steep:
 *
 *     1000 gold + 2 x spell level of EACH of mercury, sulfur, crystal, gems
 *
 * once per town per day, one slot at a time, choosing between two candidates.
 *
 * **The two candidates are derived, not stored.** They are a pure function of
 * the root seed, the day, the town and the guild level, so both are enumerable
 * as ordinary legal commands (`RESEARCH_SPELL` with `pick: 0 | 1`) and the
 * player picks by issuing one of them. That is what keeps this out of the
 * interaction machinery `LEVEL_UP` needs: there is no prompt to raise, no
 * resume token to persist, and the AI can weigh both offers with the same
 * `enumerateLegalCommands` every other decision goes through.
 *
 * It also means no new required field on `TownState`. The only state research
 * adds is `researchedThisTurn`, an optional flag in the same shape as
 * `builtThisTurn` and cleared beside it - so a save written before research
 * existed still loads, and the battle and state hashes of an untouched game do
 * not move.
 *
 * The expansion also finalises a slot (a researched spell becomes permanent and only
 * then learnable). That is deliberately not modelled: it exists to stop a
 * player re-rolling the same slot inside one turn, which the once-per-day
 * limit already prevents here.
 */

import type { ContentRegistry, SpellDef } from "../content/registry.js";
import type { SpellDefId, TownId } from "../ids.js";
import type { ResourceKind, TownState } from "../state.js";
import type { Ctx } from "./ctx.js";
import { guildLevelOf } from "./town.js";

/** Gold, and the rare resources charged per level of the spell replaced. */
export const RESEARCH_GOLD = 1000;
export const RESEARCH_RARE_PER_LEVEL = 2;
export const RESEARCH_RARES = Object.freeze(
  ["mercury", "sulfur", "crystal", "gems"] as const,
);

/** What one research at `level` costs, as a resource record. */
export function researchCost(level: number): Readonly<Partial<Record<ResourceKind, number>>> {
  const out: Partial<Record<ResourceKind, number>> = { gold: RESEARCH_GOLD };
  for (const r of RESEARCH_RARES) out[r] = RESEARCH_RARE_PER_LEVEL * level;
  return out;
}

/**
 * An integer hash, the same shape `grail.ts` uses to place the Grail: the
 * candidates have to be a function of the seed rather than of a roll, because
 * `enumerateLegalCommands` must be able to name them without advancing any RNG
 * stream (3.3 - enumeration is pure).
 */
function mix(a: number, b: number): number {
  let x = (a ^ Math.imul(b, 0x9e3779b1)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b) >>> 0;
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

function hashText(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h >>> 0;
}

/** Every spell of one level the guild could hold but does not. */
function pool(content: ContentRegistry, t: TownState, level: number): SpellDefId[] {
  const held = new Set<string>();
  for (const row of t.guildSpells) for (const s of row) held.add(s as string);
  const out: SpellDefId[] = [];
  for (const s of [...content.spells.values()].sort((a, b) => (a.id < b.id ? -1 : 1))) {
    const def: SpellDef = s;
    if (def.level !== level) continue;
    if (def.artifactOnly === true) continue;
    if (held.has(def.id as string)) continue;
    out.push(def.id);
  }
  return out;
}

/**
 * The two spells this town is offered today at this guild level.
 *
 * One entry when the pool has only one left, and none when it is empty - The expansion
 * gives no choice at level 5 either, and a guild that already holds every spell
 * of its level has nothing to research.
 */
export function researchCandidates(
  c: Ctx, t: TownState, level: number,
): readonly SpellDefId[] {
  const p = pool(c.content, t, level);
  if (p.length === 0) return [];
  if (p.length === 1) return [p[0]!];
  const seed = mix(mix(c.s.rootSeed, hashText(t.id as string)), mix(c.s.turn.day, level));
  const first = seed % p.length;
  // A second, distinct index: stepping by an offset in 1..len-1 can never
  // land back on the first, so two different spells are always offered.
  const second = (first + 1 + (mix(seed, level) % (p.length - 1))) % p.length;
  return [p[first]!, p[second]!];
}

export type ResearchIllegality =
  | { readonly code: "NO_SUCH_ENTITY"; readonly id: string }
  | { readonly code: "NOT_OWNED"; readonly id: string }
  | { readonly code: "BUILD_LIMIT_REACHED" }
  | { readonly code: "TARGET_INVALID" }
  | { readonly code: "INSUFFICIENT_RESOURCES"; readonly resource: ResourceKind;
    readonly have: number; readonly need: number };

/** Why this research may not happen, or null. Pure. */
export function researchIllegality(
  c: Ctx, id: TownId, level: number, pick: number,
): ResearchIllegality | null {
  const t = c.s.towns[id];
  if (t === undefined) return { code: "NO_SUCH_ENTITY", id: id as string };
  if (t.owner === null) return { code: "NOT_OWNED", id: id as string };
  if (t.researchedThisTurn === true) return { code: "BUILD_LIMIT_REACHED" };
  if (level < 1 || level > guildLevelOf(c, t)) return { code: "TARGET_INVALID" };
  const held = t.guildSpells[level - 1] ?? [];
  // Nothing to replace: research swaps a spell, it does not add one.
  if (held.length === 0) return { code: "TARGET_INVALID" };
  const offers = researchCandidates(c, t, level);
  if (pick < 0 || pick >= offers.length) return { code: "TARGET_INVALID" };
  const p = c.s.players[t.owner];
  if (p === undefined) return { code: "NOT_OWNED", id: id as string };
  const cost = researchCost(level);
  // Sorted, and not only to satisfy I3. `researchCost` has one construction
  // path today, so insertion order happens to be stable - but the moment a
  // second path exists (a modifier, a content override, a spread of partials)
  // the order becomes path-dependent and the refusal names a different
  // resource on two runs of the same game. Sorting now costs nothing and
  // removes the hazard rather than the symptom. It also makes the message a
  // player sees stable instead of arbitrary.
  for (const k of (Object.keys(cost) as ResourceKind[]).sort()) {
    const need = cost[k] ?? 0;
    const have = p.resources[k] ?? 0;
    if (have < need) return { code: "INSUFFICIENT_RESOURCES", resource: k, have, need };
  }
  return null;
}
